import React from 'react';
import { useParams } from 'react-router-dom';

import { Checkbox } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { Skeleton } from '@material-ui/lab';
import cx from 'classnames';
import { TrackerObjectiveValueTypes } from 'constants/assignments';
import { isNil } from 'lodash';
import { useInsightsClassroomTrackerInstanceSectionDetail } from 'sdk';

import { ReactComponent as NeedsReviewIcon } from 'pages/Teachers/ClassroomInsights/assets/alert-icon.svg';
import { ReactComponent as PublishedIcon } from 'pages/Teachers/ClassroomInsights/assets/published-icon.svg';
import { ReactComponent as UncheckIcon } from 'pages/Teachers/ClassroomInsights/assets/uncheck-icon.svg';
import CheckGradeIcon from 'pages/Teachers/ClassroomInsights/components/CheckGradeIcon';
import {
  BACKGROUND_COLOR_MAPPER,
  MASTERY_LEVELS,
  STATUS_ICON_MAPPER
} from 'pages/Teachers/ClassroomInsights/constants';
import {
  getShortPerformanceRubricLabel,
  getSortCheckboxRubricValue
} from 'utils/trackers';

import styles from './style.module.scss';

const StudentProgressBox = ({
  name,
  status,
  masteryLabel,
  grade,
  isSelected,
  isPublished,
  onClick,
  onSelect
}) => {
  const handleOnClick = () => {
    if (masteryLabel !== MASTERY_LEVELS.NEED_UPLOAD) {
      onClick();
    }
  };

  const { trackerInstanceSectionId } = useParams();
  const { data: sectionDetails } =
    useInsightsClassroomTrackerInstanceSectionDetail({
      trackerInstanceSectionId
    });

  const valueType = sectionDetails?.value_type;
  if (valueType === TrackerObjectiveValueTypes.PERFORMANCE) {
    grade = getShortPerformanceRubricLabel({ rawValue: grade });
  }

  if (valueType === TrackerObjectiveValueTypes.CHECKBOX) {
    grade = getSortCheckboxRubricValue({ value: grade });
  }

  if (status === MASTERY_LEVELS.LOADING) {
    return (
      <div className={styles.studentProgressBox}>
        <div className={styles.student}>
          <p>{name}</p>
          <div>
            <CircularProgress size={15} color="secondary" />
          </div>
        </div>
        <div className={styles.studentInfo}>
          <Skeleton variant="text" width={100} height={24} />
          <Skeleton variant="text" width={30} height={24} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cx(styles.studentProgressContainer, {
        [styles.hoverable]: status !== MASTERY_LEVELS.PENDING_UPLOAD
      })}
      onClick={handleOnClick}
    >
      <div
        className={cx(styles.studentProgressBox, {
          [styles.selectedBorder]: isSelected,
          [styles.hoverable]: status !== MASTERY_LEVELS.PENDING_UPLOAD
        })}
      >
        <div className={styles.studentRow}>
          <div className={styles.student}>
            {status !== MASTERY_LEVELS.PENDING_UPLOAD && (
              <div
                className={styles.checkboxWrapper}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={styles}>
                  <Checkbox
                    className={styles.checkbox}
                    checked={isSelected}
                    onChange={onSelect}
                    disableRipple
                    icon={<span className={styles.uncheckedIcon} />}
                    checkedIcon={
                      <div className={styles.checkedIcon}>
                        <UncheckIcon />
                      </div>
                    }
                  />
                </div>
              </div>
            )}
            <Tooltip title={name} arrow placement="top">
              <p>{name}</p>
            </Tooltip>
          </div>
          <div>{STATUS_ICON_MAPPER[status]}</div>
        </div>
        <div className={styles.studentInfo}>
          <span
            className={`${styles.status}`}
            style={{
              backgroundColor: BACKGROUND_COLOR_MAPPER[status]?.backgroundColor,
              borderColor: BACKGROUND_COLOR_MAPPER[status]?.borderColor,
              color: BACKGROUND_COLOR_MAPPER[status]?.color
            }}
          >
            {status === MASTERY_LEVELS.NEEDS_REVIEW && <NeedsReviewIcon />}
            {status}
            {isPublished && <PublishedIcon />}
          </span>
          <span className={styles.grade}>
            {valueType === TrackerObjectiveValueTypes.CHECK_GRADE &&
              (!isNil(grade) ? (
                <CheckGradeIcon grade={grade} objectiveValueType={valueType} />
              ) : (
                '-'
              ))}
            {valueType !== TrackerObjectiveValueTypes.CHECK_GRADE &&
              (!isNil(grade)
                ? [
                    TrackerObjectiveValueTypes.PERFORMANCE,
                    TrackerObjectiveValueTypes.RUBRIC_A_TO_F,
                    TrackerObjectiveValueTypes.CHECKBOX
                  ].includes(valueType)
                  ? grade?.toUpperCase()
                  : grade
                : '-')}
          </span>
        </div>
      </div>
      <VisibilityIcon
        className={styles.viewIcon}
        color="primary"
        style={{ fontSize: 40 }}
      />
    </div>
  );
};

export default StudentProgressBox;
